import { memo, useEffect, useRef } from 'react';

import * as THREE from 'three';
import { MarchingCubes } from 'three/examples/jsm/objects/MarchingCubes.js';
import { ToonShaderHatching } from 'three/examples/jsm/shaders/ToonShader.js';

import { isWebGLAvailable } from 'src/shared/components/WebGLErrorBoundary';

interface BlobLoaderProps {
  className?: string;
  width?: number;
  height?: number;
  speed?: number;
  numBlobs?: number;
}

const BlobLoader = memo(
  ({
    className,
    width = 200,
    height = 200,
    speed = 0.03,
    numBlobs = 3
  }: BlobLoaderProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const effectRef = useRef<MarchingCubes | null>(null);
    const materialRef = useRef<THREE.Material | null>(null);
    const timeRef = useRef<number>(0);
    const clockRef = useRef<THREE.Clock>(new THREE.Clock());
    const isVisibleRef = useRef<boolean>(true);
    const isTabVisibleRef = useRef<boolean>(true);

    useEffect(() => {
      const container = containerRef.current;

      if (!container || !isWebGLAvailable()) return;

      // CAMERA
      const camera = new THREE.PerspectiveCamera(45, width / height, 1, 10000);

      camera.position.set(-200, 500, 300);
      camera.lookAt(0, 0, 0);
      cameraRef.current = camera;

      // SCENE
      const scene = new THREE.Scene();

      scene.background = null; // Transparent background
      sceneRef.current = scene;

      // LIGHTS
      const light = new THREE.DirectionalLight(0xffffff, 3);

      light.position.set(0.5, 0.5, 1);
      scene.add(light);

      const ambientLight = new THREE.AmbientLight(0x323232, 3);

      scene.add(ambientLight);

      // MATERIAL - Simple hatching material
      const baseColor = new THREE.Color(0x000eef); // #000EEF
      const hatchingShaderUniforms = THREE.UniformsUtils.clone(
        ToonShaderHatching.uniforms
      );

      hatchingShaderUniforms['uDirLightPos'] = { value: light.position };
      hatchingShaderUniforms['uDirLightColor'] = { value: light.color };
      hatchingShaderUniforms['uAmbientLightColor'] = {
        value: ambientLight.color
      };
      hatchingShaderUniforms['uColor'] = { value: baseColor };
      hatchingShaderUniforms['uHatchingColor'] = { value: baseColor };

      // Modify fragment shader to apply colors
      const hatchingVertexShader = ToonShaderHatching.vertexShader;
      let hatchingFragmentShader = ToonShaderHatching.fragmentShader;

      // Add color uniform declarations
      if (!hatchingFragmentShader.includes('uniform vec3 uColor')) {
        const uniformSectionMatch =
          hatchingFragmentShader.match(/(uniform\s+[^;]+;)/);

        if (uniformSectionMatch) {
          hatchingFragmentShader = hatchingFragmentShader.replace(
            uniformSectionMatch[0],
            `${uniformSectionMatch[0]}\nuniform vec3 uColor;\nuniform vec3 uHatchingColor;`
          );
        } else {
          hatchingFragmentShader = `uniform vec3 uColor;\nuniform vec3 uHatchingColor;\n${hatchingFragmentShader}`;
        }
      } else if (
        !hatchingFragmentShader.includes('uniform vec3 uHatchingColor')
      ) {
        hatchingFragmentShader = hatchingFragmentShader.replace(
          'uniform vec3 uColor;',
          'uniform vec3 uColor;\nuniform vec3 uHatchingColor;'
        );
      }

      // Apply color to fragment shader
      const lastGlFragColorMatchHatching = hatchingFragmentShader.match(
        /gl_FragColor\s*=\s*[^;]+;/g
      );

      if (lastGlFragColorMatchHatching) {
        const lastAssignment =
          lastGlFragColorMatchHatching[lastGlFragColorMatchHatching.length - 1];

        hatchingFragmentShader = hatchingFragmentShader.replace(
          lastAssignment,
          `${lastAssignment}\n  vec3 originalColor = gl_FragColor.rgb;\n  gl_FragColor.rgb *= uColor;\n  float luminance = dot(originalColor, vec3(0.299, 0.587, 0.114));\n  float darkness = 1.0 - luminance;\n  if (darkness > 0.1 || luminance < 0.5) {\n    float redAmount = smoothstep(0.1, 0.9, darkness);\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, redAmount * 0.95);\n  }\n  if (luminance < 0.7) {\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, (0.7 - luminance) * 0.6);\n  }`
        );
      } else {
        hatchingFragmentShader = hatchingFragmentShader.replace(
          /(\s*}\s*)$/,
          '\n  vec3 originalColor = gl_FragColor.rgb;\n  gl_FragColor.rgb *= uColor;\n  float luminance = dot(originalColor, vec3(0.299, 0.587, 0.114));\n  float darkness = 1.0 - luminance;\n  if (darkness > 0.1 || luminance < 0.5) {\n    float redAmount = smoothstep(0.1, 0.9, darkness);\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, redAmount * 0.95);\n  }\n  if (luminance < 0.7) {\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, (0.7 - luminance) * 0.6);\n  }\n$1'
        );
      }

      const hatchingMaterial = new THREE.ShaderMaterial({
        uniforms: hatchingShaderUniforms,
        vertexShader: hatchingVertexShader,
        fragmentShader: hatchingFragmentShader
      });

      materialRef.current = hatchingMaterial;

      // MARCHING CUBES - Balanced resolution for hatching clarity
      const resolution = 20;
      const effect = new MarchingCubes(
        resolution,
        hatchingMaterial,
        true,
        true,
        100000
      );

      effect.position.set(0, 0, 0);
      effect.scale.set(300, 300, 300);
      effect.enableUvs = false;
      effect.enableColors = false;
      effect.isolation = 40;
      effectRef.current = effect;
      scene.add(effect);

      // RENDERER
      const renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true
      });

      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Higher pixel ratio for clearer hatching
      renderer.setSize(width, height);
      renderer.setClearColor(0x000000, 0);
      rendererRef.current = renderer;
      container.appendChild(renderer.domElement);

      // Update cubes function - simplified
      const updateCubes = (
        object: MarchingCubes,
        time: number,
        numblobs: number
      ) => {
        object.reset();

        const subtract = 12;
        const strength = 1.2 / ((Math.sqrt(numblobs) - 1) / 4 + 1);

        for (let i = 0; i < numblobs; i++) {
          const ballx =
            Math.sin(i + 1.26 * time * (1.03 + 0.5 * Math.cos(0.21 * i))) *
              0.27 +
            0.5;
          const bally =
            Math.abs(Math.cos(i + 1.12 * time * Math.cos(1.22 + 0.1424 * i))) *
            0.77;
          const ballz =
            Math.cos(i + 1.32 * time * 0.1 * Math.sin(0.92 + 0.53 * i)) * 0.27 +
            0.5;

          object.addBall(ballx, bally, ballz, strength, subtract);
        }

        object.update();
      };

      // Animation loop - optimized
      const animate = () => {
        animationFrameRef.current = requestAnimationFrame(animate);

        // Skip rendering if tab is not visible or component is not visible
        if (!isTabVisibleRef.current || !isVisibleRef.current) {
          const delta = clockRef.current.getDelta();

          timeRef.current += delta * speed * 0.5;

          return;
        }

        const delta = clockRef.current.getDelta();

        timeRef.current += delta * speed * 0.5;

        // Update cubes
        if (effectRef.current) {
          updateCubes(effectRef.current, timeRef.current, numBlobs);
        }

        // Render
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
          rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
      };

      // Intersection Observer to detect visibility
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            isVisibleRef.current = entry.isIntersecting;
          });
        },
        {
          threshold: 0.1
        }
      );

      if (container) {
        intersectionObserver.observe(container);
      }

      // Visibility API to detect tab visibility
      const handleVisibilityChange = () => {
        isTabVisibleRef.current = !document.hidden;
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      isTabVisibleRef.current = !document.hidden;

      // Start animation
      animate();

      // Cleanup
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
        if (container) {
          intersectionObserver.unobserve(container);
        }
        intersectionObserver.disconnect();

        if (
          rendererRef.current &&
          container.contains(rendererRef.current.domElement)
        ) {
          container.removeChild(rendererRef.current.domElement);
        }

        // Dispose of material
        if (materialRef.current) {
          materialRef.current.dispose();
        }

        // Dispose of renderer
        if (rendererRef.current) {
          rendererRef.current.dispose();
        }

        // Remove effect from scene
        if (effectRef.current && sceneRef.current) {
          sceneRef.current.remove(effectRef.current);
        }
      };
    }, [width, height, speed, numBlobs]);

    return (
      <div ref={containerRef} className={className} style={{ width, height }} />
    );
  }
);

BlobLoader.displayName = 'BlobLoader';

export default BlobLoader;
